import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import {
  MoreHorizontal, ChevronDown,
  MessageCircle, Bell, Siren, HeartHandshake, Heart, ShoppingBag, Search,
  Coins, LineChart, Store, Calculator,
  PlayCircle, Package, LayoutDashboard, PlusCircle, Info,
  Users,
} from "lucide-react";

/**
 * MoreNavMenu — Phase 4B · consolidates the ex-top-nav overflow into a
 * single "More ▾" popover so the header stays scannable at 1280 px.
 *
 * Items are grouped by intent — Community · Money · Utilities · Create ·
 * Site — so the popover reads like a mini site map. Every entry keeps
 * the testid it had in the flat nav so existing tests / muscle-memory
 * still resolve.
 */
const GROUPS = [
  {
    title: "Community",
    items: [
      { to: "/shop",      label: "Shopping",  Icon: ShoppingBag,    testid: "nav-shop" },
      { to: "/need",      label: "Find what I need", Icon: Search, testid: "nav-need" },
      { to: "/messages",  label: "Messages",  Icon: MessageCircle,  testid: "nav-messages" },
      { to: "/alerts",    label: "Alerts",    Icon: Bell,           testid: "nav-alerts" },
      { to: "/emergency", label: "Emergency", Icon: Siren,          testid: "nav-emergency", className: "text-red-600" },
      { to: "/welfare",   label: "Welfare",   Icon: HeartHandshake, testid: "nav-welfare",   className: "text-red-600" },
      { to: "/donate",    label: "Donate",    Icon: Heart,          testid: "nav-donate",    className: "text-rose-600" },
    ],
  },
  {
    title: "Money",
    items: [
      { to: "/findpay",      label: "FindPay",      Icon: Coins,     testid: "nav-findpay", pulse: true, className: "text-emerald-700 font-bold" },
      { to: "/rideshare",    label: "RideShare",    Icon: Coins,     testid: "nav-rideshare", pulse: true, className: "text-emerald-700 font-bold" },
      { to: "/metal-pocket", label: "Metal Pocket", Icon: Coins,     testid: "nav-metal", pulse: true },
      { to: "/prices",       label: "Prices",       Icon: LineChart, testid: "nav-prices" },
      { to: "/dealers",      label: "Dealers",      Icon: Store,     testid: "nav-dealers" },
      { to: "/erp",          label: "Ledger",       Icon: Calculator,testid: "nav-erp",   className: "text-emerald-700" },
    ],
  },
  {
    title: "Utilities",
    items: [
      { to: "/learn",      label: "Learn",      Icon: PlayCircle,      testid: "nav-learn",   className: "text-purple-600" },
      { to: "/parcels",    label: "Parcels",    Icon: Package,         testid: "nav-parcels", className: "text-blue-600" },
      { to: "/dashboard",  label: "Dashboard",  Icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/calculator", label: "Calculator", Icon: Calculator,      testid: "nav-calculator" },
      { to: "/hr",         label: "Attendance & Salary", Icon: Users,   testid: "nav-hr" },
    ],
  },
  {
    title: "Create",
    items: [
      { to: "/list-my-business", label: "List business", Icon: PlusCircle, testid: "nav-list-business" },
    ],
  },
  {
    title: "Site",
    items: [
      { to: "/about", label: "About", Icon: Info, testid: "nav-about-more" },
    ],
  },
];

export default function MoreNavMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const loc = useLocation();
  const { t } = useI18n();

  // Close on outside click + on any route change.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown",   onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown",   onEsc);
    };
  }, [open]);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div className="relative" ref={ref} data-testid="more-nav-wrap">
      <button
        onClick={() => setOpen(v => !v)}
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        aria-label={t("shell.more_nav")}
        title={t("shell.more")}
        className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-[13px] font-semibold text-stone-700 hover:bg-stone-100 ${open ? "bg-stone-100" : ""}`}
        data-testid="more-nav-btn"
      >
        <MoreHorizontal size={16} />
        <span>{t("shell.more")}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 mt-2 w-[520px] max-w-[92vw] bg-white border border-stone-200 rounded-2xl shadow-2xl z-50 p-3 grid grid-cols-2 gap-x-4 gap-y-2"
          data-testid="more-nav-panel"
        >
          {GROUPS.map(group => (
            <div key={t(`navgroup.${group.title.toLowerCase()}`, group.title)}>
              <div className="text-[10px] font-black uppercase tracking-widest text-stone-400 px-2 py-1">
                {t(`navgroup.${group.title.toLowerCase()}`, group.title)}
              </div>
              <div className="flex flex-col">
                {group.items.map(it => (
                  <Link
                    key={it.to}
                    to={it.to}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-stone-100 text-sm font-medium text-stone-700 ${it.className || ""}`}
                    data-testid={it.testid}
                    role="menuitem"
                  >
                    <it.Icon size={16} className="shrink-0"/>
                    <span>{t(`nav.${it.testid.slice(4)}`, it.label)}</span>
                    {it.pulse && (
                      <span className="ms-auto w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true"/>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
